import { AssessmentMatch, AssessmentMatchResponse } from "model/assessment";
import { ASSESSMENT_EMPLOYEE_RELATIONSHIP } from "./enums";
import { groupBy } from "./utilities";
import { BREAKPOINTS } from "./helpers/breakpoints";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  RefObject,
  ForwardedRef,
  useRef,
  useState,
  ChangeEvent,
} from "react";
import { NavigateOptions } from "react-router-dom";
import {
  selectUserEmployeeInfo,
  selectUserInfo,
} from "store/auth/signin/signin.selectors";
import { selectCompany } from "store/company/company/company.selectors";
import { selectRedirectPath } from "store/app/app/app.selectors";
import { setRedirectPath } from "store/app/app/app.store";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { AppDispatch } from "..";
import { Employee, UserInfo } from "model/user";
import { useNavigate } from "react-router-dom";
import { IntlShape, useIntl } from "react-intl";

export const useClassNames = (props: string[]): string => {
  const [c1, c2, c3, c4, c5, c6, c7, c8, c9, c10, c11, c12, c13, c14, c15] =
    props;

  return useMemo(() => {
    return [c1, c2, c3, c4, c5, c6, c7, c8, c9, c10, c11, c12, c13, c14, c15]
      .join(" ")
      .trim();
  }, [c1, c2, c3, c4, c5, c6, c7, c8, c9, c10, c11, c12, c13, c14, c15]);
};

// TODO: to discuss later on
export const usePwdValidator = (errCopy: {
  charValidation: string;
  confirmValidation: string;
}) => {
  type State = {
    pwd: {
      value: string;
      error: string | null;
    };
    confirmPwd: {
      value: string;
      error: string | null;
    };
    isValid: boolean;
  };
  type Action =
    | { type: "UPDATE_PWD"; val: string }
    | { type: "UPDATE_PWD_ERROR"; val: string | null }
    | { type: "UPDATE_CONFIRM_PWD"; val: string }
    | { type: "UPDATE_CONFIRM_PWD_ERROR"; val: string | null }
    | { type: "UPDATE_VALIDITY"; val: boolean };

  const reducer = (state: State, action: Action) => {
    switch (action.type) {
      case "UPDATE_PWD":
        return {
          ...state,
          pwd: {
            ...state.pwd,
            value: action.val,
          },
        };
      case "UPDATE_PWD_ERROR":
        return {
          ...state,
          pwd: {
            ...state.pwd,
            error: action.val,
          },
        };
      case "UPDATE_CONFIRM_PWD":
        return {
          ...state,
          confirmPwd: {
            ...state.confirmPwd,
            value: action.val,
          },
        };
      case "UPDATE_CONFIRM_PWD_ERROR":
        return {
          ...state,
          confirmPwd: {
            ...state.confirmPwd,
            error: action.val,
          },
        };
      case "UPDATE_VALIDITY":
        return {
          ...state,
          isValid: action.val,
        };
      default:
        throw new Error("Something went wrong!");
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    pwd: {
      value: "",
      error: null,
    },
    confirmPwd: {
      value: "",
      error: null,
    },
    isValid: false,
  });

  const { pwd, confirmPwd, isValid } = state;

  useEffect(() => {
    const validate = setTimeout(() => {
      // checking for pwd validity
      if (
        pwd.value.length > 0 &&
        !new RegExp(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/).test(
          pwd.value
        )
      ) {
        dispatch({ type: "UPDATE_PWD_ERROR", val: errCopy.charValidation });
        return;
      } else {
        if (pwd.error) {
          dispatch({ type: "UPDATE_PWD_ERROR", val: null });
        }
      }

      // checking for confirmation pwd validity
      if (pwd.value !== confirmPwd.value && confirmPwd.value.length > 0) {
        dispatch({
          type: "UPDATE_CONFIRM_PWD_ERROR",
          val: errCopy.confirmValidation,
        });
        return;
      } else {
        if (confirmPwd.error) {
          dispatch({
            type: "UPDATE_CONFIRM_PWD_ERROR",
            val: null,
          });
        }
      }
    }, 500);

    return () => {
      clearTimeout(validate);
    };
  }, [
    pwd.value,
    confirmPwd.value,
    errCopy,
    pwd.error,
    confirmPwd.error,
    isValid,
  ]);

  useEffect(() => {
    // updating overall validity
    const compareValues = setTimeout(() => {
      if (
        !pwd.error &&
        !confirmPwd.error &&
        pwd.value.length > 0 &&
        pwd.value === confirmPwd.value
      ) {
        dispatch({ type: "UPDATE_VALIDITY", val: true });
      } else {
        if (isValid) {
          dispatch({ type: "UPDATE_VALIDITY", val: false });
        }
      }
    }, 500);

    return () => {
      clearTimeout(compareValues);
    };
  }, [pwd.error, confirmPwd.error, pwd.value, confirmPwd.value, isValid]);

  const handleValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.persist();
      switch (event.target.name) {
        case "password":
          dispatch({ type: "UPDATE_PWD", val: event.target.value });
          break;
        case "confirm-password":
          dispatch({ type: "UPDATE_CONFIRM_PWD", val: event.target.value });
          break;
        default:
          throw new Error("Something went wrong!");
      }
    },
    []
  );

  return {
    pwd,
    confirmPwd,
    isValid,
    handleValueChange,
  };
};

export const useAppDispatch = () => useDispatch() as AppDispatch;

export const useEmployeeData = (): Employee | null => {
  const employee = useSelector(selectUserEmployeeInfo);

  return useMemo(() => {
    return employee;
  }, [employee]);
};

export const useUserData = (): UserInfo | null => {
  const user = useSelector(selectUserInfo);

  return useMemo(() => {
    return user;
  }, [user]);
};

export const useIsHeadOfDept = () => {
  const employeeData = useEmployeeData();

  return useMemo(() => {
    return employeeData && employeeData.headOfDepartment ? true : false;
  }, [employeeData]);
};

export const useCompany = () => {
  const company = useSelector(selectCompany);

  return useMemo(() => {
    return company;
  }, [company]);
};

export const useRedirectNavigate = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const redirectPath = useSelector(selectRedirectPath);

  return useCallback(
    (altPath?: string, options?: NavigateOptions) => {
      if (redirectPath) {
        navigate(redirectPath, options);
        dispatch(setRedirectPath(""));
      } else if (altPath) {
        navigate(altPath, options);
      }
    },
    [dispatch, navigate, redirectPath]
  );
};

export const useClickOutside = <T extends HTMLElement>(
  ref: RefObject<T>,
  handler: (e?: MouseEvent) => void
) => {
  const clickHandler = useCallback(
    (event: MouseEvent) => {
      const el = ref.current;

      // Do nothing if clicking ref's element or descendent elements
      if (!el || el.contains(event.target as Node)) {
        return;
      }

      handler(event);
    },
    [handler, ref]
  );

  useEffect(() => {
    window.addEventListener("click", clickHandler);
    return () => {
      window.removeEventListener("click", clickHandler);
    };
  }, [clickHandler]);
};

export const useForwardedRef = <T>(ref: ForwardedRef<T>) => {
  const innerRef = useRef(null);

  useEffect(() => {
    if (!ref) return;
    if (typeof ref === "function") {
      ref(innerRef.current);
    } else {
      ref.current = innerRef.current;
    }
  });

  return innerRef;
};

export const useViewport = () => {
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  const timer = useRef<ReturnType<typeof setTimeout>>();

  const isMobile = useMemo(
    () => windowWidth <= BREAKPOINTS.tablet,
    [windowWidth]
  );

  const isIos = useMemo(
    () =>
      navigator.platform === "iPhone" ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints)
        ? true
        : false,
    []
  );

  const handleWindowResize = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      setWindowWidth(window.innerWidth);
    }, 300);
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  return useMemo(() => {
    return { isMobile, isIos, windowWidth };
  }, [isMobile, isIos, windowWidth]);
};

export const useGroupedMatches = (matches: AssessmentMatch[]) => {
  const groupedMatches = useMemo(() => {
    const res = groupBy(matches, (item) => item.targetEmployeeId) as [
      AssessmentMatch[]
    ];
    res.forEach((matchesGroup) =>
      matchesGroup.sort((a, b) =>
        a.type === ASSESSMENT_EMPLOYEE_RELATIONSHIP.SELF
          ? -1
          : b.type === ASSESSMENT_EMPLOYEE_RELATIONSHIP.SELF
          ? 1
          : 0
      )
    );
    return res;
  }, [matches]);

  return groupedMatches;
};

export const useListedRoles = (
  intl: IntlShape,
  matches: AssessmentMatch[] | AssessmentMatchResponse[] | undefined
) => {
  if (matches) {
    const listedRoles: string[] = [];
    const roles = matches.map((evaluator) => evaluator.type);
    class Role {
      nameSingular: string;
      namePlural: string;
      count: number;
      constructor(nameSingular: string, namePlural: string, count: number) {
        this.nameSingular = intl.formatMessage({ id: nameSingular });
        this.namePlural = intl.formatMessage({ id: namePlural });
        this.count = count;
      }
    }
    const self = new Role(
      "general.roles.selfFeedback.singular",
      "general.roles.selfFeedback.plural",
      roles.filter((role) => role === "self").length
    );
    const manager = new Role(
      "general.roles.manager.singular",
      "general.roles.manager.plural",
      roles.filter((role) => role === "manager").length
    );
    const peer = new Role(
      "general.roles.peer.singular",
      "general.roles.peer.plural",
      roles.filter((role) => role === "peer").length
    );
    const report = new Role(
      "general.roles.report.singular",
      "general.roles.report.plural",
      roles.filter((role) => role === "report").length
    );
    const genericColleague = new Role(
      "general.roles.generic_colleague.singular",
      "general.roles.generic_colleague.plural",
      roles.filter((role) => role === "generic_colleague").length
    );
    const countedRoles = [self, manager, peer, report, genericColleague].filter(
      (role) => role.count > 0
    );

    countedRoles.forEach((role) =>
      listedRoles.push(
        `${role.count} ${role.count > 1 ? role.namePlural : role.nameSingular}`
      )
    );

    return (
      listedRoles.slice(0, -1).join(", ") + " and " + listedRoles.slice(-1)
    );
  }
};

export const usePrevious = <T>(value: T): T => {
  const ref: any = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

export const useToggle = (initialState: boolean = false) => {
  const [state, setState] = useState<boolean>(initialState);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const toggle = useCallback((): void => {
    if (!mountedRef.current) return;
    setState((prevState) => !prevState);
  }, []);

  return [state, toggle] as const;
};

export const useDebounce = <T>(
  initialValue: T,
  delay: number
): [T, T, React.Dispatch<T>] => {
  const [value, setValue] = useState<T>(initialValue);
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(debounce);
    };
  }, [value, delay]);

  return useMemo(
    () => [debouncedValue, value, setValue],
    [debouncedValue, value]
  );
};

export const useIsFirstRender = (): boolean => {
  const isFirst = useRef(true);
  if (isFirst.current) {
    isFirst.current = false;
    return true;
  }
  return isFirst.current;
};

type FieldsData<T extends string> = {
  name: T;
  minimum: number;
  limit: number;
  value: string;
  isFocused: boolean;
  isExceeding: boolean;
  isEnough: boolean;
};

export const useTextInputsValidator = <T extends string>(
  fieldsData: Pick<FieldsData<T>, "name" | "minimum" | "limit">[]
) => {
  const [inputFieldsData, setInputFieldsData] = useState<FieldsData<T>[]>(
    fieldsData.map((field) => ({
      ...field,
      isFocused: false,
      isExceeding: false,
      isEnough: false,
      value: "",
    }))
  );

  const intl = useIntl();

  const charactersMinimumMet = useMemo(
    () => inputFieldsData.every((fieldData) => fieldData.isEnough),
    [inputFieldsData]
  );
  const charactersLimitMet = useMemo(
    () => inputFieldsData.some((fieldData) => fieldData.isExceeding),
    [inputFieldsData]
  );

  const findTargetFieldIndex = useCallback(
    (fieldName: T) =>
      inputFieldsData.findIndex((field) => field.name === fieldName),
    [inputFieldsData]
  );
  const findTargetField = useCallback(
    (fieldName: T) => inputFieldsData.find((field) => field.name === fieldName),
    [inputFieldsData]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const fieldName = e.target.name as T;
      const value = e.target.value;
      setInputFieldsData((prev) => {
        const targetFieldIndex = findTargetFieldIndex(fieldName);
        const targetFieldObj = prev[targetFieldIndex];
        const isExceeding = value.trim().length >= targetFieldObj.limit;
        const isEnough = value.trim().length >= targetFieldObj.minimum;
        prev[targetFieldIndex] = {
          ...targetFieldObj,
          value,
          isExceeding,
          isEnough,
        };
        return [...prev];
      });
    },
    [findTargetFieldIndex]
  );

  const getCommonAttributes = useCallback(
    (fieldName: T) => ({
      name: fieldName,
      value: findTargetField(fieldName)?.value,
      onFocus: () =>
        setInputFieldsData((prev) => {
          const targetFieldIndex = findTargetFieldIndex(fieldName);
          prev[targetFieldIndex] = {
            ...prev[targetFieldIndex],
            isFocused: true,
          };
          return [...prev];
        }),
      onBlur: () =>
        setInputFieldsData((prev) => {
          const targetFieldIndex = findTargetFieldIndex(fieldName);
          prev[targetFieldIndex] = {
            ...prev[targetFieldIndex],
            isFocused: false,
          };
          return [...prev];
        }),
    }),
    [findTargetField, findTargetFieldIndex]
  );

  const getStatusMessage = useCallback(
    (fieldName: T) => {
      const targetField = findTargetField(fieldName);
      const evaluatedContent = (() => {
        const targetFieldValue = targetField?.value.trim();
        if (targetField?.isExceeding)
          return `${intl.formatMessage({
            id: "formValidatorHook.tooLong",
          })} (${targetFieldValue?.length}/${targetField?.limit})`;
        if (!targetField?.isFocused) return;
        if (!targetFieldValue?.length)
          return intl.formatMessage(
            {
              id: "formValidatorHook.minimumCharacters",
            },
            {
              amount: targetField?.minimum,
            }
          );
        if (targetField?.isEnough)
          return intl.formatMessage(
            {
              id: "formValidatorHook.charactersLeft",
            },
            {
              amount: targetField?.limit - targetFieldValue?.length,
            }
          );
        return intl.formatMessage(
          {
            id: "formValidatorHook.charactersToGo",
          },
          {
            amount: targetField?.minimum - targetFieldValue?.length,
          }
        );
      })();
      return evaluatedContent;
    },
    [findTargetField, intl]
  );

  return {
    handleChange,
    findTargetField,
    getCommonAttributes,
    getStatusMessage,
    charactersMinimumMet,
    charactersLimitMet,
  };
};
